import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, X, Pencil, Building2, Save, ChevronDown, ChevronRight, Truck, Sparkles, ChevronsDownUp, ChevronsUpDown, FileText , MapPin } from 'lucide-react';
import { api } from '../../lib/auth';
import { toast } from 'sonner';
import { DepotFormTemplateDialog } from './DepotFormTemplateDialog';

/**
 * Depot Manager — super-admin only depot CRUD scoped by customer (PostNL,
 * DPD, ...) and optional employer-firm tags (HYK / Fast-Trans / AYTU).
 *
 * Behaviour:
 *  - When `scopeFirmId` is set (toolbar firm filter is active), new depots are
 *    pre-tagged with that firm so they only appear under that firm's view.
 *    Operator can still add more firm tags from the inline edit.
 *  - Empty firm_ids = visible to ALL firms (legacy default).
 *  - Renaming a depot triggers cascade rename on its routes (server-side).
 *  - Deleting a depot is blocked by the API if any routes reference it.
 */
const errMsg = (e) => { const d = e?.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(', ') : d; };

export function DepotManagerDialog({ open, board, scopeFirmId, scopeCompanyId, onClose, onChange }) {
  const [companies, setCompanies] = useState([]);
  const [depots, setDepots] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [openCompany, setOpenCompany] = useState({}); // {companyId: bool}
  // Per-depot row expansion for the inline routes list. Keyed by depot id or
  // the synthetic string `noDepot:${companyId}` for "(Depo yok)" buckets.
  const [openDepotRoutes, setOpenDepotRoutes] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [draftFirmIds, setDraftFirmIds] = useState([]);
  const [draftLoc, setDraftLoc] = useState('');
  const [newDepot, setNewDepot] = useState({}); // {companyId: name}
  // "Yeni rota" per bucket (either depotId or `noDepot:companyId`)
  const [newRoute, setNewRoute] = useState({});
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [routeDraft, setRouteDraft] = useState({ route_code: '', region_name: '', owner_firm_id: '' });
  // Form şablonu dialog'u (Faz 1)
  const [formTemplateDepot, setFormTemplateDepot] = useState(null);
  // Internal filter — defaults to whatever firm scope opened the dialog,
  // operator can change it freely without affecting the board.
  const [mgrFirmId, setMgrFirmId] = useState(scopeFirmId || '');
  const firms = board?.firms || [];

  useEffect(() => { if (open) setMgrFirmId(scopeFirmId || ''); }, [open, scopeFirmId]);

  const refresh = async () => {
    try {
      const [cRes, dRes, rRes] = await Promise.all([
        api.get('/planning/companies'),
        api.get('/planning/depots'),
        api.get('/planning/routes'),
      ]);
      if (cRes.data?.success) setCompanies(cRes.data.data || []);
      if (dRes.data?.success) setDepots(dRes.data.data || []);
      if (rRes.data?.success) setRoutes(rRes.data.data || []);
    } catch {
      toast.error('Veriler alınamadı');
    }
  };

  useEffect(() => {
    if (open) {
      refresh();
      // If the manager is opened in scope of a specific company (inline +Yeni
      // Depo), auto-collapse all but that one. Otherwise expand all (default).
      if (scopeCompanyId) {
        setOpenCompany((s) => ({ ...s, [scopeCompanyId]: true }));
        // Scroll the new-depot input into view shortly after refresh resolves.
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="planning-depot-mgr-new-${scopeCompanyId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            try { el.focus(); } catch { /* ignore */ }
          }
        }, 250);
      } else {
        setOpenCompany({});
      }
    }
  }, [open, scopeCompanyId]);

  const depotsByCompany = useMemo(() => {
    const m = new Map();
    for (const d of depots) {
      // Internal firm filter inside the dialog. Empty firm_ids = all firms.
      if (mgrFirmId) {
        const ids = d.firm_ids || [];
        if (ids.length > 0 && !ids.includes(mgrFirmId)) continue;
      }
      if (!m.has(d.company_id)) m.set(d.company_id, []);
      m.get(d.company_id).push(d);
    }
    return m;
  }, [depots, mgrFirmId]);

  // Group routes by depot_id. Routes without a depot land under the synthetic
  // bucket `noDepot:${company_id}` so the operator can still manage them.
  const routesByBucket = useMemo(() => {
    const m = new Map();
    for (const r of routes) {
      // Respect the same firm scope so the view stays consistent.
      if (mgrFirmId && r.owner_firm_id && r.owner_firm_id !== mgrFirmId) continue;
      const key = r.depot_id || `noDepot:${r.company_id}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    for (const [, arr] of m) arr.sort((a, b) => (a.route_code || '').localeCompare(b.route_code || ''));
    return m;
  }, [routes, mgrFirmId]);

  // Which companies have at least one "(Depo yok)" route in current scope?
  const companiesWithOrphanRoutes = useMemo(() => {
    const s = new Set();
    for (const r of routes) {
      if (r.depot_id) continue;
      if (mgrFirmId && r.owner_firm_id && r.owner_firm_id !== mgrFirmId) continue;
      s.add(r.company_id);
    }
    return s;
  }, [routes, mgrFirmId]);

  const startEdit = (d) => {
    setEditingId(d.id);
    setDraftName(d.name);
    setDraftFirmIds(d.firm_ids || []);
    setDraftLoc(d.lat != null && d.lng != null ? `${d.lat}, ${d.lng}` : '');
  };
  const cancelEdit = () => {
    setEditingId(null); setDraftName(''); setDraftFirmIds([]);
  };

  const saveEdit = async (d) => {
    const m = draftLoc.trim().match(/^(-?\d+(?:\.\d+)?)\s*[,; ]\s*(-?\d+(?:\.\d+)?)$/);
    if (draftLoc.trim() && (!m || Math.abs(Number(m[1])) > 90 || Math.abs(Number(m[2])) > 180)) { toast.error('Konum "enlem, boylam" şeklinde olmalı (örn. 50.9959, 5.2033)'); return; }
    try {
      await api.patch(`/planning/depots/${d.id}`, {
        name: draftName.trim(),
        firm_ids: draftFirmIds,
        lat: m ? Number(m[1]) : null,
        lng: m ? Number(m[2]) : null,
      });
      toast.success('Depo güncellendi');
      cancelEdit();
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Güncellenemedi');
    }
  };

  const removeDepot = async (d) => {
    if (!window.confirm(`"${d.name}" deposunu silmek istediğine emin misin?`)) return;
    try {
      await api.delete(`/planning/depots/${d.id}`);
      toast.success('Depo silindi');
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Silinemedi');
    }
  };

  const addDepot = async (company) => {
    const name = (newDepot[company.id] || '').trim();
    if (!name) {
      toast.error('Depo adı boş olamaz');
      return;
    }
    try {
      await api.post('/planning/depots', {
        company_id: company.id,
        name,
        firm_ids: mgrFirmId ? [mgrFirmId] : [],
      });
      toast.success(`${company.name} altına "${name}" eklendi`);
      setNewDepot((s) => ({ ...s, [company.id]: '' }));
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Depo eklenemedi');
    }
  };

  // ── Route CRUD ────────────────────────────────────────────────────────
  const startEditRoute = (r) => {
    setEditingRouteId(r.id);
    setRouteDraft({
      route_code: r.route_code || '',
      region_name: r.region_name || '',
      owner_firm_id: r.owner_firm_id || '',
    });
  };
  const cancelEditRoute = () => {
    setEditingRouteId(null);
    setRouteDraft({ route_code: '', region_name: '', owner_firm_id: '' });
  };
  const saveEditRoute = async (r) => {
    const code = routeDraft.route_code.trim();
    if (!code) { toast.error('Rota kodu boş olamaz'); return; }
    try {
      await api.put(`/planning/routes/${r.id}`, {
        route_code: code,
        region_name: routeDraft.region_name.trim() || null,
        owner_firm_id: routeDraft.owner_firm_id || null,
      });
      toast.success('Rota güncellendi');
      cancelEditRoute();
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Güncellenemedi');
    }
  };
  const removeRoute = async (r) => {
    if (!window.confirm(`"${r.route_code}" rotasını silmek istediğine emin misin?`)) return;
    try {
      await api.delete(`/planning/routes/${r.id}`);
      toast.success('Rota silindi');
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Silinemedi');
    }
  };
  const addRoute = async (companyId, depotId /* null allowed */, explicitCode) => {
    const bucketKey = depotId || `noDepot:${companyId}`;
    const code = (explicitCode ?? newRoute[bucketKey] ?? '').trim();
    if (!code) { toast.error('Rota kodu boş olamaz'); return; }
    try {
      await api.post('/planning/routes', {
        company_id: companyId,
        depot_id: depotId,
        route_code: code,
        owner_firm_id: mgrFirmId || null,
      });
      toast.success(`"${code}" rotası eklendi`);
      setNewRoute((s) => ({ ...s, [bucketKey]: '' }));
      // If route was added without a depot, auto-open the "(Depo yok)"
      // bucket so the operator sees what they just created.
      if (!depotId) {
        setOpenDepotRoutes((s) => ({ ...s, [bucketKey]: true }));
      }
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Rota eklenemedi');
    }
  };

  // ── Company (müşteri) CRUD ─────────────────────────────────────────────
  // Yeni deploy'lanmış bir prod ortamında `planning_companies` koleksiyonu
  // boşsa operatör sıfırdan PostNL / DPD / Bpost gibi müşterileri buradan
  // ekleyebilsin. Silme ise cascade korumasından geçer (backend zaten rota
  // varsa 400 döner) — side-effect olarak orada kalan depoları temizler.
  const [newCompanyName, setNewCompanyName] = useState('');
  // Visible companies — only those scoped to the active firm (or legacy globals
  // with empty firm_ids). When `mgrFirmId` is empty ("Tüm Firmalar"), show
  // everything; in that mode the user can manage existing entries but cannot
  // create new firm-scoped ones.
  const visibleCompanies = useMemo(() => {
    if (!mgrFirmId) return companies;
    return companies.filter((c) => {
      const ids = c.firm_ids || [];
      return ids.length === 0 || ids.includes(mgrFirmId);
    });
  }, [companies, mgrFirmId]);

  const addCompany = async () => {
    const name = newCompanyName.trim();
    if (!name) { toast.error('Müşteri adı boş olamaz'); return; }
    if (!mgrFirmId) {
      toast.error('Önce sağ üstten bir firma seçin (Tüm Firmalar modunda müşteri eklenemez).');
      return;
    }
    try {
      const res = await api.post('/planning/companies', { name, firm_ids: [mgrFirmId] });
      toast.success(`"${name}" müşterisi eklendi`);
      setNewCompanyName('');
      // Auto-expand the freshly created one so the operator can immediately
      // add depots/routes inside it.
      const newId = res.data?.data?.id;
      if (newId) setOpenCompany((s) => ({ ...s, [newId]: true }));
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Müşteri eklenemedi');
    }
  };
  const removeCompany = async (company) => {
    if (!window.confirm(`"${company.name}" müşterisini arşive göndermek istediğine emin misin?\n(Geri yüklenebilir; kalıcı silmek için arşivden tek tıkla.)`)) return;
    try {
      await api.delete(`/planning/companies/${company.id}`);
      toast.success('Müşteri arşive gönderildi');
      await refresh();
      await loadArchive();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Silinemedi');
    }
  };

  // ── Arşiv (soft-deleted) müşteriler ────────────────────────────────────
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archived, setArchived] = useState([]);
  const loadArchive = async () => {
    try {
      const res = await api.get('/planning/companies?include_archived=true');
      const all = res.data?.data || [];
      setArchived(all.filter((c) => c.active === false));
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) loadArchive(); }, [open]);

  const restoreCompany = async (c) => {
    try {
      await api.post(`/planning/companies/${c.id}/restore`);
      toast.success(`"${c.name}" geri yüklendi`);
      await refresh();
      await loadArchive();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Geri yüklenemedi');
    }
  };
  const hardDeleteCompany = async (c) => {
    if (!window.confirm(`"${c.name}" KALICI olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/planning/companies/${c.id}?hard=true`);
      toast.success('Kalıcı olarak silindi');
      await loadArchive();
    } catch (err) {
      toast.error(errMsg(err) || 'Silinemedi');
    }
  };

  // ── Multi-firm bind: müşteriyi istediği kombinasyonda firmalara
  // bağlar/çıkarır. `firmId` parametresi explicit verildiği için bu fonksiyon
  // hem aktif scope'tan bağımsız çalışır hem de tek müşteri için 3 firmayı
  // sırayla toggle etmeyi mümkün kılar.
  const toggleCompanyFirm = async (company, firmId) => {
    const target = firmId || mgrFirmId;
    if (!target) { toast.error('Hangi firmaya bağlanacağı belirsiz.'); return; }
    const targetName = firms.find((f) => f.id === target)?.name || target;
    const current = Array.isArray(company.firm_ids) ? [...company.firm_ids] : [];
    const has = current.includes(target);
    const next = has ? current.filter((x) => x !== target) : [...current, target];
    try {
      await api.patch(`/planning/companies/${company.id}`, { firm_ids: next });
      toast.success(has
        ? `"${company.name}" → ${targetName} bağı kaldırıldı`
        : `"${company.name}" → ${targetName} bağlandı`);
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Güncellenemedi');
    }
  };

  // ── "Başlangıç Şablonu" — tek tıkla PostNL / DPD / Bpost / Bpack / DHL +
  // her birinin tipik deposu eklenir. Zaten mevcut olanlar 409 ile atlanır.
  // Belçika lojistik ekosistemine göre varsayılanlar hazırlandı; operatör
  // sonradan istediğini silebilir veya yenilerini ekleyebilir.
  const STARTER_TEMPLATE = [
    { name: 'PostNL', depots: ['Oevel'] },
    { name: 'DPD',    depots: ['Oevel'] },
    { name: 'Bpost',  depots: ['Oevel'] },
    { name: 'Bpack',  depots: ['Antwerpen'] },
    { name: 'DHL',    depots: ['Oevel'] },
  ];
  const [starterLoading, setStarterLoading] = useState(false);
  const applyStarterTemplate = async () => {
    if (!mgrFirmId) {
      toast.error('Başlangıç Şablonu uygulamadan önce sağ üstten bir firma seçin.');
      return;
    }
    if (!window.confirm(
      'Başlangıç Şablonu uygulanacak:\n' +
      '• Müşteriler: PostNL, DPD, Bpost, Bpack, DHL\n' +
      '• Her birine tipik 1 depo (Oevel / Antwerpen)\n\n' +
      'Zaten mevcut olanlar atlanacak. Devam?'
    )) return;
    setStarterLoading(true);
    let addedCompanies = 0;
    let addedDepots = 0;
    let skipped = 0;
    try {
      // We need the current map of slugs→ids after each insertion to link
      // depots properly. Simplest: pull once, then query by slug inside.
      let companyMap = new Map(companies.map((c) => [c.slug, c]));
      for (const tpl of STARTER_TEMPLATE) {
        let company = companyMap.get(tpl.name.toLowerCase());
        if (!company) {
          try {
            const res = await api.post('/planning/companies', { name: tpl.name, firm_ids: [mgrFirmId] });
            company = res.data?.data;
            companyMap.set(company.slug, company);
            addedCompanies += 1;
          } catch (err) {
            if (err.response?.status === 409) {
              // Someone else created it. Re-fetch and find by slug.
              const all = await api.get('/planning/companies');
              companyMap = new Map((all.data?.data || []).map((c) => [c.slug, c]));
              company = companyMap.get(tpl.name.toLowerCase());
              skipped += 1;
            } else {
              throw err;
            }
          }
        } else {
          skipped += 1;
        }
        if (!company) continue;
        for (const depotName of tpl.depots) {
          const exists = depots.some((d) => d.company_id === company.id && d.name === depotName);
          if (exists) { skipped += 1; continue; }
          try {
            await api.post('/planning/depots', {
              company_id: company.id,
              name: depotName,
              firm_ids: mgrFirmId ? [mgrFirmId] : [],
            });
            addedDepots += 1;
          } catch (err) {
            if (err.response?.status === 409) skipped += 1;
            else throw err;
          }
        }
      }
      toast.success(`Şablon uygulandı · ${addedCompanies} müşteri, ${addedDepots} depo eklendi · ${skipped} atlandı`);
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err) || 'Şablon uygulanırken hata');
    } finally {
      setStarterLoading(false);
    }
  };

  if (!open) return null;
  const scopedFirmName = mgrFirmId ? (firms.find((f) => f.id === mgrFirmId)?.name) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-3xl w-full p-0 overflow-hidden flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()} data-testid="planning-depot-manager">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-emerald-500" />
          <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold">Depo ve Rota Yönetimi</h3>
            <p className="text-[11px] text-slate-500">
              {scopedFirmName
                ? <>Yeni depolar otomatik olarak <b className="text-indigo-600">{scopedFirmName}</b> firmasına bağlanır.</>
                : 'Yeni depolar (firma seçilmediği için) tüm firmalara açık olur.'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-red-500" data-testid="planning-depot-manager-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* Internal firm filter — independent from board / left panel.
            Operator can re-scope without leaving the dialog. */}
        <div className="px-5 py-2 flex items-center flex-wrap gap-1.5 border-b border-slate-100 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/30" data-testid="planning-depot-mgr-firm-strip">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Firma kapsamı</span>
          <button
            onClick={() => setMgrFirmId('')}
            className={`text-[11px] px-2 py-0.5 rounded-full border ${!mgrFirmId ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            data-testid="planning-depot-mgr-firm-all"
          >
            Tümü
          </button>
          {firms.map((f) => (
            <button
              key={f.id}
              onClick={() => setMgrFirmId(f.id === mgrFirmId ? '' : f.id)}
              className={`text-[11px] px-2 py-0.5 rounded-full border ${mgrFirmId === f.id ? 'bg-indigo-500 text-white border-indigo-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid={`planning-depot-mgr-firm-${f.id}`}
            >
              {f.name}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
          {/* Yeni müşteri ekleme — deploy sonrası boş prod'da operatör
              PostNL / DPD / Bpost gibi müşterileri buradan başlatır. */}
          <div className="flex items-center gap-2 px-1 pb-2 border-b border-slate-100 dark:border-slate-800 mb-2">
            <Input
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addCompany(); }}
              className="h-7 text-xs"
              placeholder={mgrFirmId
                ? `Yeni müşteri (${scopedFirmName} için, ör. PostNL, DPD, Bpost)`
                : 'Önce sağ üstten bir firma seçin'}
              disabled={!mgrFirmId}
              data-testid="planning-depot-mgr-new-company"
            />
            <Button
              size="sm"
              className="h-7"
              onClick={addCompany}
              disabled={!mgrFirmId}
              title={mgrFirmId ? '' : 'Önce sağ üstten bir firma seçin'}
              data-testid="planning-depot-mgr-add-company"
            >
              <Plus className="h-3 w-3 mr-1" /> Müşteri Ekle
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={applyStarterTemplate}
              disabled={starterLoading || !mgrFirmId}
              data-testid="planning-depot-mgr-starter-template"
              title={mgrFirmId
                ? 'PostNL, DPD, Bpost, Bpack, DHL + tipik depoları tek tıkla ekle'
                : 'Önce sağ üstten bir firma seçin'}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              {starterLoading ? 'Uygulanıyor...' : 'Başlangıç Şablonu'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 ml-auto text-slate-500 hover:text-slate-800"
              onClick={() => {
                // Tüm müşteri kartlarının açık/kapalı state'ini toplu toggle eder.
                // En az 1 kart kapalıysa "hepsini aç", aksi halde "hepsini kapat".
                const allOpen = visibleCompanies.every((c) => openCompany[c.id] !== false);
                const next = {};
                visibleCompanies.forEach((c) => { next[c.id] = !allOpen; });
                setOpenCompany((s) => ({ ...s, ...next }));
              }}
              disabled={visibleCompanies.length === 0}
              data-testid="planning-depot-mgr-toggle-all"
              title={visibleCompanies.every((c) => openCompany[c.id] !== false)
                ? 'Tüm müşterileri kapat'
                : 'Tüm müşterileri aç'}
            >
              {visibleCompanies.length > 0 && visibleCompanies.every((c) => openCompany[c.id] !== false) ? (
                <>
                  <ChevronsDownUp className="h-3 w-3 mr-1" />
                  Hepsini Kapat
                </>
              ) : (
                <>
                  <ChevronsUpDown className="h-3 w-3 mr-1" />
                  Hepsini Aç
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-slate-500 hover:text-slate-800"
              onClick={() => setArchiveOpen((s) => !s)}
              data-testid="planning-depot-mgr-archive-toggle"
              title="Arşivlenmiş müşterileri görüntüle / geri yükle"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Arşiv {archived.length > 0 && `(${archived.length})`}
            </Button>
          </div>
          {/* Arşiv listesi — sadece toggle açıkken render olur. Geri yükleme +
              kalıcı silme aksiyonları içerir. */}
          {archiveOpen && (
            <div className="mb-2 p-2 rounded-md border border-amber-200 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10" data-testid="planning-depot-mgr-archive-panel">
              {archived.length === 0 ? (
                <p className="text-[11px] text-amber-700 dark:text-amber-300 italic">Arşivde müşteri yok.</p>
              ) : (
                <ul className="space-y-1">
                  {archived.map((c) => (
                    <li key={c.id} className="flex items-center gap-2 text-xs">
                      <span className="flex-1 truncate text-slate-600 dark:text-slate-300">{c.name}</span>
                      <button
                        onClick={() => restoreCompany(c)}
                        className="text-emerald-600 hover:text-emerald-700 px-2 py-0.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/30 font-medium"
                        data-testid={`planning-depot-mgr-restore-${c.id}`}
                      >
                        Geri Yükle
                      </button>
                      <button
                        onClick={() => hardDeleteCompany(c)}
                        className="text-rose-600 hover:text-rose-700 px-2 py-0.5 rounded hover:bg-rose-50 dark:hover:bg-rose-900/30 font-medium"
                        title="Kalıcı sil"
                        data-testid={`planning-depot-mgr-hard-del-${c.id}`}
                      >
                        Sil
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {visibleCompanies.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-6">
              {mgrFirmId
                ? `${scopedFirmName} için henüz müşteri yok. Üstten "Müşteri Ekle" ile başlayın.`
                : 'Henüz müşteri yok. Önce sağ üstten bir firma seçin, sonra "Müşteri Ekle" ile başlayın.'}
            </p>
          )}
          {visibleCompanies.map((c) => {
            const depotList = (depotsByCompany.get(c.id) || []).sort((a, b) => a.name.localeCompare(b.name));
            const isOpen = openCompany[c.id] !== false;
            return (
              <div key={c.id} className="border border-slate-200 dark:border-slate-700 rounded-md">
                <div className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-50/60 dark:bg-slate-800/40">
                  <button
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    onClick={() => setOpenCompany((s) => ({ ...s, [c.id]: !isOpen }))}
                    data-testid={`planning-depot-mgr-co-${c.id}`}
                  >
                    {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                    <span className="flex-1 truncate">{c.name}</span>
                    {/* "Genel" badge for legacy companies with no firm scope. */}
                    {(!Array.isArray(c.firm_ids) || c.firm_ids.length === 0) && (
                      <span className="text-[9px] px-1 py-0 rounded bg-slate-200 text-slate-500 font-bold uppercase" title="Tüm firmalar görür (eski kayıt)">Genel</span>
                    )}
                    <span className="text-[10px] text-slate-400 shrink-0">{depotList.length} depo</span>
                  </button>
                  {/* Multi-firm bind — her firma için ayrı pill. Aktif scope
                      olan firma vurgulanır; diğerleri sönük gri ama tıklanabilir.
                      Operatör tek müşteri için 3 firmayı sırayla toggle edebilir. */}
                  <div className="flex items-center gap-0.5">
                    {firms.map((f) => {
                      const ids = Array.isArray(c.firm_ids) ? c.firm_ids : [];
                      const linked = ids.includes(f.id);
                      const isActiveScope = mgrFirmId === f.id;
                      const cls = linked
                        ? (isActiveScope ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200')
                        : (isActiveScope ? 'bg-emerald-50 text-emerald-700 border border-dashed border-emerald-300 hover:bg-emerald-100' : 'bg-slate-100 text-slate-400 hover:bg-emerald-100 hover:text-emerald-700');
                      return (
                        <button
                          key={f.id}
                          onClick={() => toggleCompanyFirm(c, f.id)}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-semibold transition-colors ${cls}`}
                          title={linked ? `${f.name} bağını kaldır` : `${f.name} firmasına bağla`}
                          data-testid={`planning-depot-mgr-bind-${c.id}-${f.id}`}
                        >
                          {linked ? '✓' : '+'}{f.name}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => removeCompany(c)}
                    className="text-slate-400 hover:text-red-500 p-1"
                    title={`${c.name} müşterisini arşive gönder`}
                    data-testid={`planning-depot-mgr-del-co-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {isOpen && (
                  <div className="px-2 py-2 space-y-1.5">
                    {depotList.length === 0 && !companiesWithOrphanRoutes.has(c.id) && (
                      <p className="text-[11px] text-slate-400 italic px-2">Bu müşteride henüz depo veya rota yok.</p>
                    )}
                    {depotList.map((d) => {
                      const depotRoutes = routesByBucket.get(d.id) || [];
                      const routesOpen = openDepotRoutes[d.id] !== false;
                      if (editingId === d.id) {
                        return (
                          <div key={d.id} className="flex flex-col gap-1.5 px-2 py-1.5 rounded-md bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200">
                            <Input
                              value={draftName}
                              onChange={(e) => setDraftName(e.target.value)}
                              className="h-7 text-xs"
                              placeholder="Depo adı"
                              autoFocus
                              data-testid={`planning-depot-mgr-edit-name-${d.id}`}
                            />
                            <div className="flex flex-wrap gap-1.5 items-center">
                              <span className="text-[10px] uppercase text-slate-500 mr-1">Firmalar:</span>
                              {firms.map((f) => {
                                const on = draftFirmIds.includes(f.id);
                                return (
                                  <button
                                    key={f.id}
                                    onClick={() => setDraftFirmIds((s) => on ? s.filter((x) => x !== f.id) : [...s, f.id])}
                                    className={`text-[10px] px-1.5 py-0.5 rounded-full border ${on ? 'bg-indigo-500 text-white border-indigo-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                  >
                                    {f.name}
                                  </button>
                                );
                              })}
                              {draftFirmIds.length === 0 && <span className="text-[10px] text-slate-400 italic">Tüm firmalar</span>}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <Input value={draftLoc} onChange={(e) => setDraftLoc(e.target.value)} className="h-7 text-xs" placeholder="Depo konumu: enlem, boylam (Google Maps'ten kopyalayın)" data-testid={`planning-depot-mgr-edit-loc-${d.id}`} />
                            </div>
                            <div className="flex justify-end gap-1.5 mt-1">
                              <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={cancelEdit}>Vazgeç</Button>
                              <Button size="sm" className="h-6 text-[11px]" onClick={() => saveEdit(d)} data-testid={`planning-depot-mgr-save-${d.id}`}>
                                <Save className="h-3 w-3 mr-1" /> Kaydet
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={d.id} className="rounded-md border border-slate-100 dark:border-slate-800">
                          <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`planning-depot-mgr-row-${d.id}`}>
                            <button onClick={() => setOpenDepotRoutes((s) => ({ ...s, [d.id]: !routesOpen }))} className="text-slate-400 hover:text-slate-600">
                              {routesOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate flex items-center gap-1">{d.name}{d.lat != null && <MapPin className="h-3 w-3 text-emerald-600" title={`Konum: ${d.lat}, ${d.lng}`} />}</p>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {(d.firm_ids || []).map((fid) => {
                                  const f = firms.find((x) => x.id === fid);
                                  if (!f) return null;
                                  return (
                                    <span key={fid} className="text-[9px] px-1 py-0 rounded bg-indigo-50 text-indigo-700 font-bold uppercase border border-indigo-100">
                                      {f.name}
                                    </span>
                                  );
                                })}
                                {(d.firm_ids || []).length === 0 && (
                                  <span className="text-[9px] text-slate-400 italic">tüm firmalar</span>
                                )}
                              </div>
                            </div>
                            <span className="text-[10px] text-slate-400">{depotRoutes.length} rota</span>
                            <button onClick={() => setFormTemplateDepot(d)} className="text-slate-400 hover:text-blue-600 p-1" title="Form Şablonu" data-testid={`planning-depot-mgr-form-${d.id}`}>
                              <FileText className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => startEdit(d)} className="text-slate-400 hover:text-emerald-600 p-1" title="Düzenle" data-testid={`planning-depot-mgr-edit-${d.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => removeDepot(d)} className="text-slate-400 hover:text-red-500 p-1" title="Sil" data-testid={`planning-depot-mgr-del-${d.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {routesOpen && (
                            <RouteList
                              bucketKey={d.id}
                              companyId={c.id}
                              depotId={d.id}
                              routes={depotRoutes}
                              firms={firms}
                              editingRouteId={editingRouteId}
                              routeDraft={routeDraft}
                              setRouteDraft={setRouteDraft}
                              onStartEdit={startEditRoute}
                              onCancelEdit={cancelEditRoute}
                              onSaveEdit={saveEditRoute}
                              onRemove={removeRoute}
                              newRoute={newRoute}
                              setNewRoute={setNewRoute}
                              onAdd={addRoute}
                            />
                          )}
                        </div>
                      );
                    })}
                    {/* (Depo yok) bucket — only when there's at least one orphan route */}
                    {companiesWithOrphanRoutes.has(c.id) && (() => {
                      const bucketKey = `noDepot:${c.id}`;
                      const orphanRoutes = routesByBucket.get(bucketKey) || [];
                      const routesOpen = openDepotRoutes[bucketKey] !== false;
                      return (
                        <div className="rounded-md border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/20" data-testid={`planning-depot-mgr-no-depot-${c.id}`}>
                          <button
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600"
                            onClick={() => setOpenDepotRoutes((s) => ({ ...s, [bucketKey]: !routesOpen }))}
                          >
                            {routesOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            <span className="italic">(Depo yok)</span>
                            <span className="ml-auto text-[10px] text-slate-400">{orphanRoutes.length} rota</span>
                          </button>
                          {routesOpen && (
                            <RouteList
                              bucketKey={bucketKey}
                              companyId={c.id}
                              depotId={null}
                              routes={orphanRoutes}
                              firms={firms}
                              editingRouteId={editingRouteId}
                              routeDraft={routeDraft}
                              setRouteDraft={setRouteDraft}
                              onStartEdit={startEditRoute}
                              onCancelEdit={cancelEditRoute}
                              onSaveEdit={saveEditRoute}
                              onRemove={removeRoute}
                              newRoute={newRoute}
                              setNewRoute={setNewRoute}
                              onAdd={addRoute}
                            />
                          )}
                        </div>
                      );
                    })()}
                    <div className="mt-2 px-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Input
                          value={newDepot[c.id] || ''}
                          onChange={(e) => setNewDepot((s) => ({ ...s, [c.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') addDepot(c); }}
                          className="h-7 text-xs"
                          placeholder={scopedFirmName ? `Yeni depo (${scopedFirmName})` : 'Yeni depo adı'}
                          data-testid={`planning-depot-mgr-new-${c.id}`}
                        />
                        <Button size="sm" className="h-7" onClick={() => addDepot(c)} data-testid={`planning-depot-mgr-add-${c.id}`}>
                          <Plus className="h-3 w-3 mr-1" /> Depo Ekle
                        </Button>
                      </div>
                      {/* Hızlı rota ekleme — depo gerektirmez. Eklenen rota
                          "(Depo yok)" bucket'ında belirir, sonradan istenen
                          depoya taşınabilir (route düzenleme ile). Bu sayede
                          AYTU > Pharma gibi henüz deposu olmayan müşterilere
                          tek tıkla rota açılabilir. */}
                      <div className="flex items-center gap-2">
                        <Input
                          value={newRoute[`inline:${c.id}`] || ''}
                          onChange={(e) => setNewRoute((s) => ({ ...s, [`inline:${c.id}`]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            const code = (newRoute[`inline:${c.id}`] || '').trim();
                            if (!code) return;
                            setNewRoute((s) => ({ ...s, [`inline:${c.id}`]: '' }));
                            addRoute(c.id, null, code);
                          }}
                          className="h-7 text-[11px] font-mono"
                          placeholder={scopedFirmName ? `Hızlı rota (${scopedFirmName}, depo yok)` : 'Hızlı rota kodu (depo yok)'}
                          data-testid={`planning-depot-mgr-quick-route-${c.id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => {
                            const code = (newRoute[`inline:${c.id}`] || '').trim();
                            if (!code) { toast.error('Rota kodu boş olamaz'); return; }
                            setNewRoute((s) => ({ ...s, [`inline:${c.id}`]: '' }));
                            addRoute(c.id, null, code);
                          }}
                          data-testid={`planning-depot-mgr-quick-route-add-${c.id}`}
                          title="Depo gerekmeden direkt müşteriye bağlı rota ekle"
                        >
                          <Truck className="h-3 w-3 mr-1" /> Rota Ekle
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-2 border-t border-slate-100 dark:border-slate-700 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>Kapat</Button>
        </div>
      </Card>

      {/* Form Şablonu sub-dialog (Faz 1) */}
      <DepotFormTemplateDialog
        open={!!formTemplateDepot}
        depot={formTemplateDepot}
        onClose={() => setFormTemplateDepot(null)}
      />
    </div>
  );
}

/**
 * Compact route list rendered inside a depot (or "(Depo yok)") row.
 * Supports inline edit + delete + add. Uses the parent's newRoute map so the
 * "Yeni rota" input state survives across re-renders without lifting refs.
 */
function RouteList({ bucketKey, companyId, depotId, routes, firms, editingRouteId, routeDraft, setRouteDraft, onStartEdit, onCancelEdit, onSaveEdit, onRemove, newRoute, setNewRoute, onAdd }) {
  return (
    <div className="px-2 pb-2 pt-1 space-y-1 border-t border-slate-100 dark:border-slate-800" data-testid={`planning-route-list-${bucketKey}`}>
      {routes.length === 0 && (
        <p className="text-[10px] text-slate-400 italic px-1">Bu depoda rota yok.</p>
      )}
      {routes.map((r) => editingRouteId === r.id ? (
        <div key={r.id} className="flex flex-col gap-1 px-1.5 py-1.5 rounded bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200">
          <div className="flex gap-1.5 items-center">
            <Input
              value={routeDraft.route_code}
              onChange={(e) => setRouteDraft((s) => ({ ...s, route_code: e.target.value }))}
              className="h-6 text-[11px] w-24 font-mono"
              placeholder="Kod"
              autoFocus
            />
            <Input
              value={routeDraft.region_name}
              onChange={(e) => setRouteDraft((s) => ({ ...s, region_name: e.target.value }))}
              className="h-6 text-[11px] flex-1"
              placeholder="Bölge (ops.)"
            />
          </div>
          <div className="flex flex-wrap gap-1 items-center">
            <span className="text-[9px] uppercase text-slate-500">Rota sahibi:</span>
            <button
              onClick={() => setRouteDraft((s) => ({ ...s, owner_firm_id: '' }))}
              className={`text-[9px] px-1.5 py-0.5 rounded-full border ${!routeDraft.owner_firm_id ? 'bg-slate-500 text-white border-slate-500' : 'border-slate-200 text-slate-600'}`}
            >
              Tümü
            </button>
            {firms.map((f) => (
              <button
                key={f.id}
                onClick={() => setRouteDraft((s) => ({ ...s, owner_firm_id: f.id }))}
                className={`text-[9px] px-1.5 py-0.5 rounded-full border ${routeDraft.owner_firm_id === f.id ? 'bg-indigo-500 text-white border-indigo-500' : 'border-slate-200 text-slate-600'}`}
              >
                {f.name}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-1 mt-1">
            <Button size="sm" variant="ghost" className="h-5 text-[10px]" onClick={onCancelEdit}>Vazgeç</Button>
            <Button size="sm" className="h-5 text-[10px]" onClick={() => onSaveEdit(r)} data-testid={`planning-route-mgr-save-${r.id}`}>
              <Save className="h-2.5 w-2.5 mr-0.5" /> Kaydet
            </Button>
          </div>
        </div>
      ) : (
        <div key={r.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`planning-route-mgr-row-${r.id}`}>
          <Truck className="h-3 w-3 text-slate-400 shrink-0" />
          <span className="text-[11px] font-mono font-bold">{r.route_code}</span>
          {r.region_name && <span className="text-[10px] text-slate-500 truncate">{r.region_name}</span>}
          {r.owner_firm_name && (
            <span className="text-[9px] px-1 py-0 rounded bg-indigo-50 text-indigo-700 font-bold uppercase border border-indigo-100">
              {r.owner_firm_name}
            </span>
          )}
          <span className="ml-auto flex items-center gap-0.5">
            <button onClick={() => onStartEdit(r)} className="text-slate-400 hover:text-emerald-600 p-0.5" title="Düzenle" data-testid={`planning-route-mgr-edit-${r.id}`}>
              <Pencil className="h-3 w-3" />
            </button>
            <button onClick={() => onRemove(r)} className="text-slate-400 hover:text-red-500 p-0.5" title="Sil" data-testid={`planning-route-mgr-del-${r.id}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        </div>
      ))}
      <div className="flex items-center gap-1 px-1.5 pt-1">
        <Input
          value={newRoute[bucketKey] || ''}
          onChange={(e) => setNewRoute((s) => ({ ...s, [bucketKey]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') onAdd(companyId, depotId); }}
          className="h-6 text-[11px] font-mono"
          placeholder="Yeni rota kodu (ör. BP-01)"
          data-testid={`planning-route-mgr-new-${bucketKey}`}
        />
        <Button size="sm" className="h-6 text-[10px]" onClick={() => onAdd(companyId, depotId)} data-testid={`planning-route-mgr-add-${bucketKey}`}>
          <Plus className="h-2.5 w-2.5 mr-0.5" /> Rota
        </Button>
      </div>
    </div>
  );
}
